/* The queue (designs/intake-ui-parity.md UP4–UP6, one board since
   intake-unified-form.md UF5/UF6): the prototype's stats, filter bar,
   sortable table, roadmap toggle, and CSV export, over the shared
   board-view logic. Rests grouped by owning team with "Nobody owns this
   yet" first. Presentational — every predicate, filter, sort, and summary
   lives in board-view.ts. */
import { useEffect, useMemo, useRef, useState, type JSX } from "react";
import {
  HEALTHS,
  PHASE_TARGETS,
  PLATFORM_STATUSES,
  PRIORITIES,
  SUB_STATUSES,
  labelFor,
  type VocabOption,
} from "../../../shared/capabilities";
import type { CanonicalTeam } from "../../../shared/canonical-teams";
import type { CapabilityFiling } from "../../../shared/capabilities";
import type { Product } from "../../../shared/products";
import type { Me } from "./api";
import {
  COL_HELP,
  DELIVERY_TOTAL,
  EMPTY_FILTERS,
  FILTER_HELP,
  QUEUE_CONCEPTS,
  STAT_HELP,
  boardOrder,
  myEffortIds,
  UNOWNED_LABEL,
  csvFor,
  filterRows,
  hasFilters,
  isRouted,
  isShared,
  ownersChip,
  ownersLabel,
  sortRows,
  type BoardFilters,
  type BoardView,
  type SortKey,
} from "./board-view";
import { Drawer, type Stepper } from "./Drawer";
import { RoadmapView } from "./RoadmapView";
import { BoardLanes } from "./BoardLanes";

function Stat({
  k,
  v,
  sub,
  small,
  help,
  onClick,
  pressed,
}: {
  k: string;
  v: string | number;
  sub: string;
  small?: boolean;
  help?: string;
  /* a card that is also a filter toggle (FR4: Yours) */
  onClick?: () => void;
  pressed?: boolean;
}): JSX.Element {
  return (
    <div
      className={`stat${onClick !== undefined ? " stat-toggle" : ""}${pressed === true ? " on" : ""}`}
      title={help}
      {...(onClick !== undefined
        ? {
            role: "button",
            tabIndex: 0,
            "aria-pressed": pressed === true,
            onClick,
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            },
          }
        : {})}
    >
      <div className="k">{k}</div>
      <div className="v" style={small ? { fontSize: 18 } : undefined}>
        {v}
      </div>
      <div className="sub">{sub}</div>
    </div>
  );
}

function FilterSelect({
  value,
  onChange,
  placeholder,
  options,
  label,
  help,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  options: ReadonlyArray<VocabOption>;
  label: string;
  help: string;
}): JSX.Element {
  return (
    <select
      className="f-select"
      aria-label={label}
      title={help}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function SortTh({
  label,
  k,
  help,
  sortKey,
  sortDir,
  onSort,
}: {
  label: string;
  k: SortKey;
  help: string;
  sortKey: SortKey | null;
  sortDir: "asc" | "desc";
  onSort: (k: SortKey) => void;
}): JSX.Element {
  const active = sortKey === k;
  return (
    <th title={help}>
      <button className={`th-sort${active ? " active" : ""}`} onClick={() => onSort(k)}>
        {label}
        <span className="th-arrow">{active ? (sortDir === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}

export function Board({
  rows,
  teams,
  products = [],
  me,
  notice,
  error,
  onNew,
  onAbout,
  onReload,
}: {
  rows: BoardView[];
  teams: CanonicalTeam[];
  /* the product list (UF21), for the filter */
  products?: Product[];
  me: Me | null;
  notice: string | null;
  error: string | null;
  onNew: () => void;
  onAbout: () => void;
  /* a drawer edit changed a record — the module re-reads the board */
  onReload: () => void;
}): JSX.Element {
  const [view, setView] = useState<"table" | "roadmap" | "board">("table");
  const [openId, setOpenId] = useState<string | null>(null);
  const [f, setF] = useState<BoardFilters>(EMPTY_FILTERS);
  /* the resting order is BY OWNER (UF5): "Nobody owns this yet" first, then
     one group per owning team. A single global score ranking would compare
     asks worked by different engineers (MD12-d). Array.sort is stable and
     rows arrive score-ranked, so each group keeps its own score order. */
  const [sortKey, setSortKey] = useState<SortKey | null>("owner");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!exportOpen) return;
    const onDown = (e: MouseEvent): void => {
      /* the menu div always renders, so the ref is set whenever this runs */
      if (!exportRef.current!.contains(e.target as Node)) setExportOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [exportOpen]);

  const set = <K extends keyof BoardFilters>(key: K, value: BoardFilters[K]): void =>
    setF((s) => ({ ...s, [key]: value }));
  const toggleSort = (key: SortKey): void => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      /* score is most useful high-first — the prototype's default */
      setSortDir(key === "score" ? "desc" : "asc");
    }
  };

  const live = useMemo(() => rows.filter((r) => !r.archived), [rows]);
  /* FR4: whose efforts are whose — the filings read carries each origin
     stamp; null means it failed, and the Yours card says so */
  const [filings, setFilings] = useState<CapabilityFiling[] | null>(null);
  useEffect(() => {
    let live = true;
    fetch("/api/intake/capabilities/filings")
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return (await res.json()) as CapabilityFiling[];
      })
      .then((list) => {
        if (live) setFilings(Array.isArray(list) ? list : null);
      })
      .catch(() => {
        if (live) setFilings(null);
      });
    return () => {
      live = false;
    };
  }, [rows]);
  const mine = useMemo(
    () => myEffortIds(rows, filings, me?.user ? (me.user.ids ?? [me.user.userId]) : []),
    [rows, filings, me],
  );
  const filtered = useMemo(() => filterRows(rows, f, mine.ids), [rows, f, mine]);
  /* FR4: compact by default; More columns is a per-viewer convenience */
  const [more, setMore] = useState<boolean>(() => {
    try {
      return localStorage.getItem("intake-queue-columns") === "more";
    } catch {
      return false;
    }
  });
  const toggleMore = (): void => {
    setMore((m) => {
      try {
        localStorage.setItem("intake-queue-columns", m ? "compact" : "more");
      } catch {
        /* a convenience — the queue works without it */
      }
      return !m;
    });
  };
  const sorted = useMemo(() => sortRows(filtered, sortKey, sortDir), [filtered, sortKey, sortDir]);

  /* the open effort's place in what the reader is looking at
     (intake-drawer-stepping.md DS1) — no stepper when it has left the list */
  /* the Board view reads lane by lane, so it steps lane by lane (IG11) */
  const order = view === "board" ? boardOrder(sorted) : sorted;
  const openIndex = openId === null ? -1 : order.findIndex((r) => r.id === openId);
  const stepper: Stepper | undefined =
    openIndex < 0
      ? undefined
      : {
          index: openIndex,
          total: order.length,
          onPrev: openIndex > 0 ? () => setOpenId(order[openIndex - 1]!.id) : null,
          onNext: openIndex < order.length - 1 ? () => setOpenId(order[openIndex + 1]!.id) : null,
        };
  /* DS5: keep the open row on screen, so closing leaves the reader there */
  useEffect(() => {
    if (openId === null) return;
    /* the table row or the Board card — whichever view is showing */
    document.querySelector(`[data-row-id="${openId}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [openId]);

  /* CSV of the full board — the sandbox-free anchor-click download (UP6) */
  const doExport = (): void => {
    setExportOpen(false);
    const blob = new Blob([csvFor(rows)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    /* the blob URL is revoked even if the click throws (PR-92 review) */
    try {
      const a = document.createElement("a");
      a.href = url;
      a.download = "hermes-intake.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
  };

  const isAdmin = me?.roles.includes("admin") ?? false;
  const teamOptions = teams.map((t) => ({ value: t.id, label: t.name }));

  return (
    <div data-intake-dashboard>
      <div className="page-head">
        <h1>Intake queue</h1>
        <p>
          Every large effort across the robots, in one place — so two teams never build the
          same thing.{" "}
          <button className="link-inline" onClick={onAbout}>
            Why this intake? →
          </button>
        </p>
        {/* QH4: the queue's three load-bearing concepts, closed until asked */}
        <div className="queue-concepts">
          {QUEUE_CONCEPTS.map((c) => (
            <details key={c.id} className="concept" data-queue-concept={c.id}>
              <summary>
                <span className="concept-i">ⓘ</span> {c.title}
              </summary>
              <div className="concept-body">
                {c.body.map((p) => (
                  <p key={p}>{p}</p>
                ))}
                <button type="button" className="concept-more" onClick={onAbout}>
                  More in About intake →
                </button>
              </div>
            </details>
          ))}
        </div>
      </div>

      <div className="stats">
        {/* "Teams", not "Vehicle teams" — the canonical list holds platform
            teams too — and only CURRENT ones counted */}
        <Stat k="Teams" v={teams.filter((t) => !t.retired).length} sub="controlled list" help={STAT_HELP.teams} />
        <Stat k="Efforts" v={live.length} sub="on the register" help={STAT_HELP.efforts} />
        <Stat
          k="Nobody owns yet"
          v={live.filter((r) => !isRouted(r)).length}
          sub="not routed · includes Suggested"
          help={STAT_HELP.unowned}
        />
        <Stat
          k="Shared candidates"
          v={live.filter(isShared).length}
          sub="common / multi-team"
          help={STAT_HELP.shared}
        />
        <Stat
          k="Committed"
          v={live.filter((r) => r.tracking?.committed ?? false).length}
          sub="this cycle"
          help={STAT_HELP.committed}
        />
        {/* FR4: what happened to mine — the identity chip already names the reader */}
        <Stat
          k="Yours"
          v={live.filter((r) => mine.ids.has(r.id)).length}
          sub={
            mine.filingsKnown
              ? `${live.filter((r) => mine.ids.has(r.id) && r.tracking?.groomed === true).length} groomed`
              : "point of contact only — who filed what could not be read"
          }
          help={STAT_HELP.yours}
          onClick={() => set("mineOnly", !f.mineOnly)}
          pressed={f.mineOnly}
        />
      </div>

      <div className="filters">
        <input
          className="f-search"
          placeholder="Search efforts…"
          aria-label="Search efforts"
          title={FILTER_HELP.search}
          value={f.q}
          onChange={(e) => set("q", e.target.value)}
        />
        <FilterSelect label="Filter by team" help={FILTER_HELP.team} value={f.team} onChange={(v) => set("team", v)} placeholder="All teams" options={teamOptions} />
        {products.length > 0 && (
          <FilterSelect
            label="Filter by product"
            help={FILTER_HELP.product}
            value={f.product}
            onChange={(v) => set("product", v)}
            placeholder="All products"
            options={products.map((p) => ({ value: p.id, label: p.name }))}
          />
        )}
        {/* the routing lens (UF5): who is expected to deliver it */}
        <FilterSelect label="Filter by owning team" help={FILTER_HELP.owner} value={f.owner} onChange={(v) => set("owner", v)} placeholder="All owners" options={teamOptions} />
        <FilterSelect label="Filter by status" help={FILTER_HELP.status} value={f.status} onChange={(v) => set("status", v)} placeholder="All statuses" options={PLATFORM_STATUSES} />
        <FilterSelect label="Filter by phase" help={FILTER_HELP.phase} value={f.phase} onChange={(v) => set("phase", v)} placeholder="All phases" options={PHASE_TARGETS} />
        <label className="f-check" title={FILTER_HELP.shared}>
          <input type="checkbox" checked={f.sharedOnly} onChange={(e) => set("sharedOnly", e.target.checked)} /> Shared
        </label>
        <label className="f-check" title={FILTER_HELP.unowned}>
          <input type="checkbox" checked={f.unownedOnly} onChange={(e) => set("unownedOnly", e.target.checked)} /> Nobody owns yet
        </label>
        <label className="f-check" title={FILTER_HELP.committed}>
          <input type="checkbox" checked={f.committedOnly} onChange={(e) => set("committedOnly", e.target.checked)} /> Committed
        </label>
        <label className="f-check" title={FILTER_HELP.mine}>
          <input type="checkbox" checked={f.mineOnly} onChange={(e) => set("mineOnly", e.target.checked)} /> Mine
        </label>
        <label className="f-check" title={FILTER_HELP.archived}>
          <input type="checkbox" checked={f.showArchived} onChange={(e) => set("showArchived", e.target.checked)} /> Archived
        </label>
        {hasFilters(f) && (
          <button className="f-clear" onClick={() => setF(EMPTY_FILTERS)}>
            Clear
          </button>
        )}
      </div>

      {notice !== null && (
        <p className="capture-notice" data-capture-notice>
          {notice}
        </p>
      )}
      {error !== null && <p className="capture-error">{error}</p>}

      <div className="card">
        <div className="card-head">
          <h2>Efforts</h2>
          <span className="count">
            {filtered.length}
            {filtered.length !== rows.length ? ` of ${rows.length}` : ""} shown
          </span>
          <div className="view-toggle">
            <button className={view === "table" ? "on" : ""} onClick={() => setView("table")}>
              Table
            </button>
            <button className={view === "roadmap" ? "on" : ""} onClick={() => setView("roadmap")}>
              Roadmap
            </button>
            <button className={view === "board" ? "on" : ""} onClick={() => setView("board")}>
              Board
            </button>
          </div>
          {view === "table" && (
            <button className="btn small ghost" onClick={toggleMore}>
              {more ? "Fewer columns" : "More columns"}
            </button>
          )}
          <div className="export-menu" ref={exportRef}>
            <button className="btn small ghost" onClick={() => setExportOpen((o) => !o)}>
              Export ▾
            </button>
            {exportOpen && (
              <div className="export-dropdown">
                <div className="export-dropdown-label">Export all capabilities</div>
                <button onClick={doExport}>CSV (.csv)</button>
              </div>
            )}
          </div>
          <button className="btn small" onClick={onNew}>
            + New effort
          </button>
        </div>

        <div className="card-body" style={{ padding: view === "table" ? 0 : 16 }}>
          {rows.length === 0 ? (
            <div className="empty">
              <div className="big">No efforts registered yet</div>
              <div>Register the first one — in plain words; the platform managers route it.</div>
              <div style={{ marginTop: 16 }}>
                <button className="btn" onClick={onNew}>
                  + New effort
                </button>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty">
              <div className="big">Nothing matches these filters</div>
              <button className="f-clear" style={{ marginTop: 10 }} onClick={() => setF(EMPTY_FILTERS)}>
                Clear filters
              </button>
            </div>
          ) : view === "roadmap" ? (
            <RoadmapView rows={sorted} onOpen={setOpenId} />
          ) : view === "board" ? (
            <BoardLanes rows={sorted} isAdmin={isAdmin} openId={openId} onOpen={setOpenId} />
          ) : (
            <div className="table-wrap">
              <table className="intake-board">
                <thead>
                  <tr>
                    <SortTh label="Owners" k="owner" help={COL_HELP.owner} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    <SortTh label="Effort" k="name" help={COL_HELP.name} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    <SortTh label="Team" k="team" help={COL_HELP.team} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    {more && <SortTh label="Product" k="product" help={COL_HELP.product} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />}
                    {more && <SortTh label="Contact" k="contact" help={COL_HELP.contact} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />}
                    {more && <SortTh label="Shared" k="shared" help={COL_HELP.shared} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />}
                    <SortTh label="Score" k="score" help={COL_HELP.score} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    <SortTh label="Priority" k="priority" help={COL_HELP.priority} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    <SortTh label="Status" k="status" help={COL_HELP.status} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
                    {more && <SortTh label="Health" k="health" help={COL_HELP.health} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />}
                    {more && <SortTh label="Phase" k="phase" help={COL_HELP.phase} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />}
                    {more && <SortTh label="Delivery" k="delivery" help={COL_HELP.delivery} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />}
                    {more && <SortTh label="Groomed" k="groomed" help={COL_HELP.groomed} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r) => (
                    <tr
                      key={r.id}
                      data-row-id={r.id}
                      aria-current={r.id === openId ? "true" : undefined}
                      className={`row-click${r.archived ? " row-retired" : ""}${r.id === openId ? " row-open" : ""}`}
                      tabIndex={0}
                      role="button"
                      aria-label={`Open ${r.name}`}
                      onClick={() => setOpenId(r.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setOpenId(r.id);
                        }
                      }}
                    >
                      {/* the routing cell (UF6): red = nobody owns it yet, orange =
                          the requester's suggestion awaiting groom, plain = routed */}
                      {(() => {
                        const chip = ownersChip(r);
                        return (
                          <td
                            className={`owner-cell ${chip.kind}`}
                            data-owner-state={chip.kind}
                            title={
                              chip.kind === "routed"
                                ? ownersLabel(r.ownerNames)
                                : chip.kind === "suggested"
                                  ? `Suggested by the requester: ${ownersLabel(r.ownerNames)}. Not routed until an intake manager grooms it.`
                                  : `${UNOWNED_LABEL}. ${STAT_HELP.unowned}`
                            }
                          >
                            {chip.text}
                          </td>
                        );
                      })()}
                      <td>
                        <div className="feat-name">{r.name}</div>
                        <div className="feat-tags">
                          {r.archived && <span className="tinytag archived">archived</span>}
                          {(r.tracking?.committed ?? false) && (
                            <span className="tinytag committed">committed</span>
                          )}
                          {r.tracking?.jiraEpic != null && <span className="tinytag epic">epic</span>}
                        </div>
                      </td>
                      <td>{r.teamName}</td>
                      {more && (
                        <td className={r.productName === null ? "cell-dash" : undefined} data-product-cell>
                          {r.productName ?? "—"}
                        </td>
                      )}
                      {more && (
                        <td
                          className={r.pointOfContact === null ? "not-recorded" : undefined}
                          data-contact-state={r.pointOfContact === null ? "missing" : "named"}
                        >
                          {r.pointOfContact === null ? "Not recorded" : r.pointOfContact.name}
                        </td>
                      )}
                      {more && (
                        <td>
                          {isShared(r) ? (
                            <span className="shared-yes">
                              Shared{r.stakeholderCount > 0 ? ` +${r.stakeholderCount}` : ""}
                            </span>
                          ) : (
                            <span className="cell-dash">—</span>
                          )}
                        </td>
                      )}
                      <td>
                        <span className="score-cell">{r.score}</span>
                      </td>
                      <td>{labelFor(PRIORITIES, r.priority)}</td>
                      <td>
                        {r.tracking?.platformStatus != null
                          ? labelFor(PLATFORM_STATUSES, r.tracking.platformStatus)
                          : labelFor(SUB_STATUSES, r.subStatus)}
                      </td>
                      {more && (
                        <>
                      <td>
                        {r.tracking?.health != null ? (
                          <span
                            className={`health-badge ${r.tracking.health}`}
                            title={labelFor(HEALTHS, r.tracking.health)}
                          >
                            <span className={`health-dot ${r.tracking.health}`} />
                            {r.tracking.health[0]!.toUpperCase()}
                          </span>
                        ) : (
                          <span className="cell-dash">—</span>
                        )}
                      </td>
                      <td>
                        {r.tracking?.roadmapPhase != null && r.tracking.roadmapPhase !== r.phaseTarget ? (
                          <>
                            <div>{labelFor(PHASE_TARGETS, r.tracking.roadmapPhase)}</div>
                            <div className="phase-req">req: {labelFor(PHASE_TARGETS, r.phaseTarget)}</div>
                          </>
                        ) : (
                          labelFor(PHASE_TARGETS, r.tracking?.roadmapPhase ?? r.phaseTarget)
                        )}
                      </td>
                      <td>
                        <div className="dc-cell" title={`${r.deliveryDone} of ${DELIVERY_TOTAL} delivery stages`}>
                          <div className="dc-bar">
                            <div
                              className="dc-fill"
                              style={{ width: `${(r.deliveryDone / DELIVERY_TOTAL) * 100}%` }}
                            />
                          </div>
                          <span className="dc-num">
                            {r.deliveryDone}/{DELIVERY_TOTAL}
                          </span>
                        </div>
                      </td>
                      <td>
                        {(r.tracking?.groomed ?? false) ? (
                          <span className="groomed-yes">Groomed</span>
                        ) : (
                          <span className="groomed-no">Ungroomed</span>
                        )}
                      </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {openId !== null && (
        <Drawer
          id={openId}
          isAdmin={isAdmin}
          me={me}
          teams={teams}
          productLabel={rows.find((r) => r.id === openId)?.productName ?? null}
          stepper={stepper}
          onClose={() => setOpenId(null)}
          onChanged={onReload}
        />
      )}
    </div>
  );
}
